/**
 * Kitwright — world bounding box maths.
 *
 * The exporter needs the extent of a build in world space to centre it and to
 * sit its base on y = 0. Doing that with `Box3.expandByObject` would depend on
 * live meshes and up-to-date world matrices; here it is arithmetic over each
 * piece's LOCAL geometry box plus its (position, rotY, uniform scale), which
 * is exactly what the project JSON stores. That makes it testable, and it
 * makes an export reproducible from a saved file with no scene at all.
 *
 * Rotating the box and taking its extents (rather than rotating the extents)
 * matters: a wall rotated 45° occupies a wider footprint than its unrotated
 * min/max, and axis-aligned maths on the raw extents would clip it.
 */

/**
 * Apply the piece transform to a local point. Uniform scale, then a rotation
 * about Y, then translation — the same order `Matrix4.compose` uses, and the
 * same handedness as three's `R_y`: x' = x·cos + z·sin, z' = −x·sin + z·cos.
 */
export function transformPoint(p, pos, rotY, scale) {
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const x = p[0] * scale;
  const y = p[1] * scale;
  const z = p[2] * scale;
  return [x * c + z * s + pos[0], y + pos[1], -x * s + z * c + pos[2]];
}

/** The eight corners of an axis-aligned box given as `{min, max}` triples. */
export function boxCorners(box) {
  const out = [];
  for (let i = 0; i < 8; i++) {
    out.push([
      i & 1 ? box.max[0] : box.min[0],
      i & 2 ? box.max[1] : box.min[1],
      i & 4 ? box.max[2] : box.min[2],
    ]);
  }
  return out;
}

/**
 * Union of every piece's transformed local box.
 *
 * @param items `[{ box: {min, max}, pos, rotY, scale }]`
 * @returns `{min, max}` in world space, or **null** when there is nothing to
 *   measure. Null rather than an empty-at-infinity box on purpose: callers
 *   branch on "nothing to export", and an infinite box propagates NaN into the
 *   centring offset instead of failing where the problem is.
 */
export function worldBox(items) {
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  let any = false;

  for (const it of items) {
    if (!it || !it.box) continue;
    const scale = it.scale === undefined ? 1 : it.scale;
    const rotY = it.rotY || 0;
    const pos = it.pos || [0, 0, 0];
    for (const c of boxCorners(it.box)) {
      const w = transformPoint(c, pos, rotY, scale);
      for (let k = 0; k < 3; k++) {
        if (w[k] < min[k]) min[k] = w[k];
        if (w[k] > max[k]) max[k] = w[k];
      }
      any = true;
    }
  }
  return any ? { min, max } : null;
}

/**
 * The translation that puts a box's footprint centre over the origin with its
 * base on y = 0 — what "Center at origin" in the export dialog means. Y is
 * `-min.y`, not `-centre.y`, so a model drops onto the floor of the importing
 * engine rather than sinking half-way through it.
 *
 * `+ 0` on each component collapses `-0`, which an already-centred build
 * produces and `JSON.stringify` faithfully writes into a glTF `translation`.
 */
export function centerOffset(box) {
  if (!box) return [0, 0, 0];
  return [
    -(box.min[0] + box.max[0]) / 2 + 0,
    -box.min[1] + 0,
    -(box.min[2] + box.max[2]) / 2 + 0,
  ];
}
